import {Component, effect, Input, OnChanges, OnDestroy, OnInit, signal, SimpleChanges} from '@angular/core';
import {MatTableDataSource, MatTableModule} from '@angular/material/table';
import {DashboardType} from '@app/core/enums/dashboard-types.enum';
import {CommonDirectives} from '@app/shared/directives/common-directives';
import {AppointmentSearchParams} from "@app/shared/interfaces/appointmentSearchParams.interface";
import {appointmentType} from "@app/shared/components/generic-dashboard/generic-dashboard.component";
import {of, Subject, takeUntil} from "rxjs";
import {Appointment, NgAppointments, NgWcSearch, WcSearch} from "@app/shared/interfaces/appointment.interface";
import {PageSearchSortOptions} from "@app/shared/interfaces/pageSearchSortOptions.interface";
import {MatSort, MatSortHeader, Sort} from "@angular/material/sort";
import {WorkCompAppointmentsService} from "@app/features/workCompDashboard/services/work-comp-appointments.service";
import {
  AppointmentDetailModalComponent
} from "@app/shared/components/appointment-detail-modal/appointment-detail-modal.component";
import {MatDialog} from "@angular/material/dialog";
import {LoadSpinnerWrapperComponent} from "@app/shared/components/load-spinner-wrapper/load-spinner-wrapper.component";
import {rxResource} from "@angular/core/rxjs-interop";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {PaginationComponent} from "@app/shared/components/pagination/pagination.component";

type PatientSearchColumn =
  | 'claimNo'
  | 'name'
  | 'dob'
  | 'employer'
  | 'insurance'
  | 'iowaOrthoProvider'
  | 'date'
  | 'status'
  | 'visit'
  | 'location'
  | 'referringProvider';

const WORK_COMP_COLUMNS: PatientSearchColumn[] = [
  'claimNo',
  'name',
  'dob',
  'employer'
];

const REFERRAL_COLUMNS: PatientSearchColumn[] = [
  'name',
  'dob',
  'date',
  'status',
  'visit',
  'location',
  'iowaOrthoProvider',
  'referringProvider'
];

@Component({
  selector: 'app-patient-search-list',
  imports: [MatTableModule, CommonDirectives, LoadSpinnerWrapperComponent, MatSort, MatProgressSpinner, PaginationComponent, MatSortHeader],
  templateUrl: './patient-search-list.component.html',
  styleUrl: './patient-search-list.component.scss'
})
export class PatientSearchListComponent implements OnInit, OnChanges, OnDestroy {
  @Input() dashboardType: DashboardType = DashboardType.WorkComp;
  @Input() dataSource: any[] = [];
  @Input() title?: string;
  @Input() search: string = '';
  @Input() emptyTableMessage = "No records found. Please adjust your filters or search criteria and try again.";

  $ngDestroy = new Subject();

  totalCount: number = 0;
  totalPages: number = 0;

  displayedColumns: PatientSearchColumn[] = [...WORK_COMP_COLUMNS];
  loading = false;

  secondarySortOptions = {
    sortField: "name",
    sortDirection: "asc"
  }

  readonly columnHeaders: Record<PatientSearchColumn, string> = {
    claimNo: 'CLAIM #',
    name: 'PATIENT NAME',
    dob: 'DOB',
    employer: 'EMPLOYER',
    insurance: 'INSURANCE',
    iowaOrthoProvider: 'IOWA ORTHO PROVIDER',
    date: 'APPT DATE & TIME',
    status: 'STATUS',
    visit: 'VISIT',
    location: 'APPT LOCATION',
    referringProvider: 'REFERRING PROVIDER'
  };

  private readonly valueKeys: Record<PatientSearchColumn, string> = {
    claimNo: 'policyNumber',
    name: 'name',
    dob: 'dateOfBirth',
    employer: 'employer',
    insurance: 'insurance',
    iowaOrthoProvider: 'iowaOrthoProvider',
    date: 'date',
    status: 'status',
    visit: 'visit',
    location: 'location',
    referringProvider: 'referringProvider'
  };

  readonly defaultPageSize: number = 25;

  pageSearchSortOptions: PageSearchSortOptions = {
    pageNumber: 1,
    pageSize: this.defaultPageSize,
    searchTerm: '',
    sortField: 'claimNo',
    sortDirection: 'asc',
    secondarySortField: "name",
    secondarySortDirection: 'asc'
  }

  resourceSortOptions = signal<PageSearchSortOptions>({
    pageNumber: 1,
    pageSize: this.defaultPageSize,
    searchTerm: '',
    sortField: 'claimNo',
    sortDirection: 'asc',
    secondarySortField: "name",
    secondarySortDirection: "asc"
  });

  resourceData = rxResource<NgWcSearch, PageSearchSortOptions>({
    params: () => this.resourceSortOptions(),
    stream: (params) => {
      const searchTerm = (params.params.searchTerm ?? '').trim();
      // Don't fetch any appointments if nothing is searched.
      if(searchTerm === ''){
        this.emptyTableMessage = 'Enter search criteria above to view results.';
        const searchData: NgWcSearch = {
          items: [],
          count: 0
        }
        return of(searchData);
      }
      this.emptyTableMessage = "No records found. Please adjust your filters or search criteria and try again.";
      const now = new Date().toISOString();
      const formatted = now.slice(0, 19);

      var minDate = new Date();
      minDate.setMonth(minDate.getMonth()-6);
      const minDateFormatted = minDate.toISOString().slice(0, 19);

      var appointmentParams : AppointmentSearchParams = {
        minDate: minDateFormatted,
        page: params.params.pageNumber.toString(),
        limit: params.params.pageSize.toString(),
        searchText: searchTerm,
        orderBy: `${params.params.sortField} ${params.params.sortDirection}, ${params.params.secondarySortField} ${params.params.secondarySortDirection}`
      }
      return this.workCompAppointmentsService.patientSearch$(appointmentParams);
    }
  });

  constructor(private workCompAppointmentsService: WorkCompAppointmentsService, private dialog: MatDialog) {
    effect(() => {
      const data = this.resourceData.value();
      if (data) {
        this.dataSource = data.items;
        this.totalPages = Math.ceil(data.count / this.defaultPageSize);
      } else {
        this.dataSource = [];
      }
    });
  }

  ngOnInit(): void {
    //this.loadAppointments();
  }

  ngOnDestroy(): void {
    this.$ngDestroy.next(true);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['showReferringProvider']) {
      this.syncDisplayedColumns();
    }

    if(changes['search']) {
      this.resourceSortOptions.update((current) => ({...current, pageNumber: 1, searchTerm: (this.search ?? '').trim()}));
    }
  }

  public onSortChange(sort: Sort){
    const direction = sort.direction;
    if (direction !== 'asc' && direction !== 'desc') return;

    let nextSortField = this.resourceSortOptions().sortField;
    let nextSecondarySortField = this.resourceSortOptions().secondarySortField;
    switch(sort.active){
      case 'claimNo':
        nextSortField = 'claimNo';
        nextSecondarySortField = 'name';
        break;
      case 'name':
        nextSortField = 'name';
        nextSecondarySortField = 'claimNo';
        break;
      case 'employer':
        nextSortField = 'employer';
        nextSecondarySortField = 'name';
        break;
      default:
        return;
    }

    this.resourceSortOptions.update((current) => ({
      ...current,
      pageNumber: 1,
      sortField: nextSortField,
      sortDirection: direction,
      secondarySortField: nextSecondarySortField,
      secondarySortDirection: 'asc'
    }));
  }

  onPageChange(newPage: number) {
    this.resourceSortOptions.update((current) => ({...current, pageNumber: newPage}));
  }

  private loadAppointments(): void {
    const now = new Date().toISOString();
    const formatted = now.slice(0, 19);

    var minDate = new Date();
    minDate.setMonth(minDate.getMonth()-6);
    const minDateFormatted = minDate.toISOString().slice(0, 19);

    var appointmentParams : AppointmentSearchParams = {
        minDate: minDateFormatted,
        page: this.pageSearchSortOptions.pageNumber.toString(),
        limit: this.pageSearchSortOptions.pageSize.toString(),
        searchText: this.pageSearchSortOptions.searchTerm,
        orderBy: `${this.pageSearchSortOptions.sortField} ${this.pageSearchSortOptions.sortDirection}, ${this.secondarySortOptions.sortField} ${this.secondarySortOptions.sortDirection}`
      }



    // Don't fetch any appointments if nothing is searched.
    if(this.pageSearchSortOptions.searchTerm == ''){
      this.emptyTableMessage = 'Enter search criteria above to view results.';
      return;
    }

     this.workCompAppointmentsService.patientSearch$(appointmentParams).pipe(takeUntil(this.$ngDestroy))
      .subscribe((appointments: NgWcSearch) => {
        this.dataSource = appointments.items ?? [];
        this.totalCount = appointments.count;
        this.totalPages = Math.ceil(appointments.count / this.defaultPageSize);
        if(this.totalCount == 0) this.emptyTableMessage = 'No records found. Please adjust your filters or search criteria and try again.';
      });
  }

  onRowClicked(row: any) {
    this.openAppointmentInfoModal(row.patientId, row.name, row.dateOfBirth, row.caseNumber);
  }

  get resolvedTitle(): string {
    return this.title ?? (this.isReferralDashboard() ? 'Patient Search' : 'Search Results');
  }

  get horizontalRuleClass(): string {
    return this.isReferralDashboard()
      ? 'bg-green w-full min-w-[705px] h-[3px] border-none'
      : 'bg-green';
  }

  get horizontalRuleStyle(): string | null {
    return this.isReferralDashboard()
      ? null
      : 'width: 100%; min-width: 560px; height: 3px; border: none;';
  }

  getCellValue(row: any, column: PatientSearchColumn): string {
    const key = this.valueKeys[column];
    const value = row?.[key];
    return value == null ? '' : String(value);
  }

  private syncDisplayedColumns(): void {
    this.displayedColumns = this.isReferralDashboard()
      ? [...REFERRAL_COLUMNS]
      : [...WORK_COMP_COLUMNS];
  }

  private isReferralDashboard(): boolean {
    return this.dashboardType === DashboardType.Referral;
  }

  openAppointmentInfoModal(patientId: any, patientName: any, dateOfBirth: any, caseNumber: string) {
    const dialogRef = this.dialog.open(AppointmentDetailModalComponent, {
      width: '700px',
      autoFocus: false,
      disableClose: false,
    });

    dialogRef.componentInstance.appointmentDetailParams = {
      patientName: patientName,
      dateOfBirth: dateOfBirth,
      appointmentType: appointmentType.PatientSearch
    }

    dialogRef.componentInstance.detailSignal.set({
      appointmentId: null,
      patientId: patientId,
      caseNumber: caseNumber,
      appointmentType: appointmentType.PatientSearch
    });
  }
}
